import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FirstRunState } from "@eduviaos/shared";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase C1a — `GET /me/first-run` query.
 *
 * Drives the FirstRunWelcomePage's tenant-config summary. Disabled for
 * super-admins (whose synthetic platform tenant has no first-run state)
 * and during the auth-loading window so we don't issue a guaranteed-403
 * round-trip on cold load.
 */
export function useFirstRun() {
  const { status, user } = useAuth();
  const enabled = status === "authed" && user?.role !== "super_admin";
  return useQuery<FirstRunState>({
    queryKey: ["first-run", "me"],
    queryFn: async () => api.get<FirstRunState>("/me/first-run"),
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Phase C1a — `POST /me/first-run/complete` mutation.
 *
 * On success: (1) overwrite the `first-run/me` query cache with the
 * post-flip projection, and (2) flip the local AuthProvider flag so
 * the AppShell guard relaxes its redirect on the very next render.
 */
export function useCompleteFirstRun() {
  const queryClient = useQueryClient();
  const { markFirstRunComplete } = useAuth();
  return useMutation<FirstRunState>({
    mutationKey: ["first-run", "complete"],
    mutationFn: async () => api.post<FirstRunState>("/me/first-run/complete"),
    onSuccess: (data) => {
      queryClient.setQueryData<FirstRunState>(["first-run", "me"], data);
      markFirstRunComplete();
    },
  });
}
